import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { ModalRoot } from './ModalRoot';
import { Z_DIALOGS_HIGH } from '@/lib/zIndex';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  tone = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const confirmClass =
    tone === 'danger'
      ? 'bg-accent-emergency hover:bg-accent-emergency-hover'
      : 'bg-accent-dispatch hover:bg-accent-dispatch-hover';

  return (
    <AnimatePresence>
      {open && (
        <ModalRoot
          onClose={onCancel}
          zIndex={Z_DIALOGS_HIGH}
          contentClass="w-full max-w-md rounded-lg border border-border-strong bg-bg-secondary p-6"
        >
            <div className="mb-4 flex items-start gap-3">
              <span
                className={[
                  'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
                  tone === 'danger'
                    ? 'bg-accent-emergency/15 text-accent-emergency'
                    : 'bg-accent-dispatch/15 text-accent-dispatch',
                ].join(' ')}
              >
                <AlertTriangle className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <h3 id="confirm-title" className="font-display text-lg font-semibold">
                  {title}
                </h3>
                <p id="confirm-desc" className="mt-1 text-sm text-fg-secondary">
                  {description}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md border border-border-strong bg-bg-elevated px-4 py-2 text-sm font-semibold text-fg-primary transition hover:bg-bg-elevated/70"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                autoFocus
                className={[
                  'rounded-md px-4 py-2 text-sm font-semibold text-white transition',
                  confirmClass,
                ].join(' ')}
              >
                {confirmLabel}
              </button>
            </div>
        </ModalRoot>
      )}
    </AnimatePresence>
  );
}
